import React, { useState, useEffect } from "react";
import { apiService } from "../services/apiService";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import {
  ClockIcon,
  CheckCircleIcon,
  PlusCircleIcon,
  FolderIcon,
  ArrowPathIcon,
  ChartBarIcon,
  SparklesIcon,
  CalendarIcon,
} from "@heroicons/react/24/outline";
import { PaginationControls } from "../components/common/PaginationControls";

interface ActivityHistoryItem {
  id: number;
  name: string;
  status: string;
  projectName?: string;
  workflowInstanceName?: string;
  startedAt?: string;
  completedAt?: string;
  completedUserIds?: string; // JSON array: "[1,5,12]"
  addedAt?: string;
  addedByUserId?: number;
  isAdHoc?: boolean;
  userDurations?: string;
}

const ACTIVITY_HISTORY_PAGE_SIZE = 10;

const ActivityHistory: React.FC = () => {
  const { t } = useTranslation(["activities", "dashboard", "common", "tasks"]);
  const { user } = useAuth();
  const [activities, setActivities] = useState<ActivityHistoryItem[]>([]);
  const [filter, setFilter] = useState<"all" | "completed" | "added">("all");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (!user) return;

    const loadActivityHistory = async () => {
      try {
        setLoading(true);
        const data = await apiService.getUserActivityHistory(user.id);
        setActivities(data);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };

    loadActivityHistory();
  }, [user]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, activities.length]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "COMPLETED":
        return {
          bg: "bg-green-50",
          text: "text-green-700",
          border: "border-green-200",
          icon: <CheckCircleIcon className="w-4 h-4" />,
          label: t("dashboard:completed"),
        };
      case "ACTIVE":
      case "IN_PROGRESS":
        return {
          bg: "bg-blue-50",
          text: "text-blue-700",
          border: "border-blue-200",
          icon: <ArrowPathIcon className="w-4 h-4 animate-spin" />,
          label:
            status === "ACTIVE" ? t("common:active") : t("dashboard:inProgress"),
        };
      case "PENDING":
        return {
          bg: "bg-gray-50",
          text: "text-gray-700",
          border: "border-gray-200",
          icon: <ClockIcon className="w-4 h-4" />,
          label: t("dashboard:pending"),
        };
      case "PENDING_APPROVAL":
        return {
          bg: "bg-amber-50",
          text: "text-amber-700",
          border: "border-amber-200",
          icon: <ClockIcon className="w-4 h-4" />,
          label: t("dashboard:pendingApproval"),
        };
      case "REJECTED":
        return {
          bg: "bg-red-50",
          text: "text-red-700",
          border: "border-red-200",
          icon: <ClockIcon className="w-4 h-4" />,
          label: t("tasks:status.rejected"),
        };
      default:
        return {
          bg: "bg-gray-50",
          text: "text-gray-700",
          border: "border-gray-200",
          icon: <ClockIcon className="w-4 h-4" />,
          label: status,
        };
    }
  };

  const parseDateTimeString = (value?: string): Date | null => {
    if (!value) return null;
    const normalized = value.includes("T") ? value : value.replace(" ", "T");
    const parsed = new Date(normalized);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const calculateBreakHours = (
    breaks: Array<{ start: string; end: string }>,
    rangeStart?: string,
    rangeEnd?: string
  ): number => {
    if (!breaks || breaks.length === 0) return 0;
    const activityStart = parseDateTimeString(rangeStart);
    const activityEnd = parseDateTimeString(rangeEnd);

    const intervals = breaks
      .map((entry) => {
        const start = parseDateTimeString(entry.start);
        const end = parseDateTimeString(entry.end);
        if (!start || !end || end <= start) return null;
        let adjustedStart = start;
        let adjustedEnd = end;
        if (activityStart && adjustedStart < activityStart) {
          adjustedStart = activityStart;
        }
        if (activityEnd && adjustedEnd > activityEnd) {
          adjustedEnd = activityEnd;
        }
        if (adjustedEnd <= adjustedStart) return null;
        return { start: adjustedStart, end: adjustedEnd };
      })
      .filter(Boolean) as Array<{ start: Date; end: Date }>;

    if (intervals.length === 0) return 0;
    intervals.sort((a, b) => a.start.getTime() - b.start.getTime());
    let totalMs = 0;
    let current = intervals[0];
    for (let i = 1; i < intervals.length; i += 1) {
      const next = intervals[i];
      if (next.start <= current.end) {
        if (next.end > current.end) {
          current.end = next.end;
        }
      } else {
        totalMs += current.end.getTime() - current.start.getTime();
        current = next;
      }
    }
    totalMs += current.end.getTime() - current.start.getTime();
    return totalMs / (1000 * 60 * 60);
  };

  const formatBreakDuration = (hours: number): string => {
    if (hours <= 0) return "";
    if (hours < 1) {
      return `${Math.round(hours * 60)} ${t("dashboard:minutes")}`;
    }
    return `${hours.toFixed(2)} ${t("dashboard:hours")}`;
  };

  const getUserBreakSummary = (activity: ActivityHistoryItem): string | null => {
    if (!activity.userDurations || !user?.id) return null;
    try {
      const durations = JSON.parse(activity.userDurations);
      const userData = durations[user.id];
      if (!userData || typeof userData !== "object") return null;
      const breaks = Array.isArray(userData.breaks) ? userData.breaks : [];
      if (!breaks.length) return null;
      const breakHours = calculateBreakHours(
        breaks,
        userData.startTime || activity.startedAt,
        userData.endTime || activity.completedAt
      );
      return breakHours > 0 ? formatBreakDuration(breakHours) : null;
    } catch {
      return null;
    }
  };

  const filteredActivities = activities.filter((activity) => {
    if (filter === "completed") {
      // Check if user is in completedUserIds JSON array
      if (activity.completedUserIds) {
        try {
          const completedIds: number[] = JSON.parse(activity.completedUserIds);
          return completedIds.includes(user?.id || 0);
        } catch (e) {
          return false;
        }
      }
      return false;
    } else if (filter === "added") {
      // Filter ad-hoc activities added by this user
      return activity.isAdHoc === true && activity.addedByUserId === user?.id;
    }
    return true;
  });

  const completedCount = activities.filter((a) => {
    if (a.completedUserIds) {
      try {
        const completedIds: number[] = JSON.parse(a.completedUserIds);
        return completedIds.includes(user?.id || 0);
      } catch {
        return false;
      }
    }
    return false;
  }).length;

  const addedCount = activities.filter(
    (a) => a.isAdHoc === true && a.addedByUserId === user?.id
  ).length;
  const totalPages = Math.max(
    1,
    Math.ceil(filteredActivities.length / ACTIVITY_HISTORY_PAGE_SIZE)
  );
  const paginatedActivities = filteredActivities.slice(
    (currentPage - 1) * ACTIVITY_HISTORY_PAGE_SIZE,
    currentPage * ACTIVITY_HISTORY_PAGE_SIZE
  );

  if (!user) return null;

  return (
    <div className="app-page bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <ClockIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t("dashboard:activityHistory")}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {t("dashboard:viewAllYourActivities")}
              </p>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-3 mb-6">
          <button
            onClick={() => setFilter("all")}
            className={`group relative px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 ${
              filter === "all"
                ? "bg-blue-600 text-white shadow-lg"
                : "bg-white text-gray-700 border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50 shadow-md hover:shadow-lg"
            }`}
          >
            <div className="flex items-center space-x-2">
              <ChartBarIcon className="w-5 h-5" />
              <span>{t("dashboard:all")}</span>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  filter === "all"
                    ? "bg-white/30 text-white"
                    : "bg-blue-100 text-blue-700"
                }`}
              >
                {activities.length}
              </span>
            </div>
            {filter === "all" && (
              <div className="absolute inset-0 bg-white/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            )}
          </button>

          <button
            onClick={() => setFilter("completed")}
            className={`group relative px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 ${
              filter === "completed"
                ? "bg-green-600 text-white shadow-lg"
                : "bg-white text-gray-700 border-2 border-gray-200 hover:border-green-300 hover:bg-green-50 shadow-md hover:shadow-lg"
            }`}
          >
            <div className="flex items-center space-x-2">
              <CheckCircleIcon className="w-5 h-5" />
              <span>{t("activities:completedActivities")}</span>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  filter === "completed"
                    ? "bg-white/30 text-white"
                    : "bg-green-100 text-green-700"
                }`}
              >
                {completedCount}
              </span>
            </div>
            {filter === "completed" && (
              <div className="absolute inset-0 bg-white/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            )}
          </button>

          <button
            onClick={() => setFilter("added")}
            className={`group relative px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 ${
              filter === "added"
                ? "bg-orange-600 text-white shadow-lg"
                : "bg-white text-gray-700 border-2 border-gray-200 hover:border-orange-300 hover:bg-orange-50 shadow-md hover:shadow-lg"
            }`}
          >
            <div className="flex items-center space-x-2">
              <PlusCircleIcon className="w-5 h-5" />
              <span>{t("activities:addedActivities")}</span>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  filter === "added"
                    ? "bg-white/30 text-white"
                    : "bg-orange-100 text-orange-700"
                }`}
              >
                {addedCount}
              </span>
            </div>
            {filter === "added" && (
              <div className="absolute inset-0 bg-white/20 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            )}
          </button>
        </div>

        {/* Activities List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600 font-medium">{t("common:loading")}...</p>
            </div>
          </div>
        ) : filteredActivities.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <SparklesIcon className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              {filter === "all"
                ? t("activities:noActivitiesYet")
                : filter === "completed"
                ? t("activities:noCompletedActivities")
                : t("activities:noAddedActivities")}
            </h3>
            <p className="text-gray-600">
              {filter === "all"
                ? "Henüz aktivite geçmişiniz yok"
                : filter === "completed"
                ? "Henüz tamamlanmış aktivite yok"
                : "Henüz eklenmiş aktivite yok"}
            </p>
          </div>
        ) : (
            <div className="space-y-3">
              {paginatedActivities.map((activity, index) => {
                const statusBadge = getStatusBadge(activity.status);
                const breakSummary = getUserBreakSummary(activity);
                return (
                <div
                  key={activity.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 animate-in fade-in-50 slide-in-from-bottom-2"
                  style={{ animationDelay: `${index * 30}ms` }}
                >
                  <div className="p-5">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      {/* Left: Activity Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-3">
                          {activity.isAdHoc ? (
                            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <PlusCircleIcon className="w-5 h-5 text-orange-600" />
                            </div>
                          ) : (
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <FolderIcon className="w-5 h-5 text-blue-600" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h3 className="text-base font-semibold text-gray-900 mb-1 truncate">
                              {activity.name}
                            </h3>
                            <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                              {activity.projectName && (
                                <div className="flex items-center gap-1">
                                  <FolderIcon className="w-4 h-4 text-gray-400" />
                                  <span className="font-medium">
                                    {activity.projectName}
                                  </span>
                                </div>
                              )}
                              {activity.workflowInstanceName && (
                                <>
                                  {activity.projectName && (
                                    <span className="text-gray-400">•</span>
                                  )}
                                  <span className="text-gray-500">
                                    {activity.workflowInstanceName}
                                  </span>
                                </>
                              )}
                              {activity.isAdHoc && (
                                <>
                                  <span className="text-gray-400">•</span>
                                  <span className="px-2 py-0.5 bg-orange-50 text-orange-700 border border-orange-200 rounded-full text-xs font-medium">
                                    Ad-Hoc
                                  </span>
                                </>
                              )}
                              {breakSummary && (
                                <>
                                  <span className="text-gray-400">|</span>
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-50 text-orange-700 border border-orange-200 rounded-full text-xs font-medium">
                                    <ClockIcon className="w-3 h-3" />
                                    {t("dashboard:breakTotal")}: {breakSummary}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Right: Status & Date */}
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div
                          className={`px-3 py-1.5 rounded-lg border ${statusBadge.bg} ${statusBadge.text} ${statusBadge.border} flex items-center gap-2 font-medium text-sm`}
                        >
                          {statusBadge.icon}
                          <span>{statusBadge.label}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-sm text-gray-500">
                          <CalendarIcon className="w-4 h-4" />
                          <span>
                            {activity.completedAt
                              ? new Date(activity.completedAt).toLocaleDateString(
                                  "tr-TR"
                                )
                              : activity.startedAt
                              ? new Date(activity.startedAt).toLocaleDateString(
                                  "tr-TR"
                                )
                              : activity.addedAt
                              ? new Date(activity.addedAt).toLocaleDateString(
                                  "tr-TR"
                                )
                              : "-"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredActivities.length}
              itemsPerPage={ACTIVITY_HISTORY_PAGE_SIZE}
              onPageChange={setCurrentPage}
              accent="blue"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityHistory;

